import { useQuery } from '@apollo/client';
import * as _ from 'lodash-es';
import React, { useState } from 'react';
import DraftsList from './DraftsList';
import * as styles from './DraftsView.module.scss';
import PageLayout from '../app/layout/PageLayout';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import PageContent from '../components/PageContent';
import PageTitle from '../components/PageTitle';
import { gql } from '../graphql/generated/gql';
import { DraftsFilter, DraftsOrder } from '../graphql/generated/graphql';
import draftArchivedIcon from '../icons/non-prefilled/16x16/draftArchived.svg';
import draftsIcon from '../icons/non-prefilled/16x16/drafts.svg';
import memberIcon from '../icons/non-prefilled/16x16/member.svg';
import orderIcon from '../icons/non-prefilled/16x16/order.svg';
import teamIcon from '../icons/non-prefilled/16x16/team.svg';
import PostFilterMenu from '../post/PostFilterMenu';
import PostSectionHeader from '../post/PostSectionHeader';
import { UIActions } from '../ui/uiSlice';
import ConnectionHelper from '../utils/graphql/ConnectionHelper';
import PageMeta from '../utils/PageMeta';

const DRAFTS_VIEW_QUERY = gql(/** @complexity 34 */ `
  query DraftsView(
    $afterCursor: String
    $filter: DraftsFilter
    $orderBy: DraftsOrder
  ) {
    currentSession {
      user {
        id
        drafts(
          first: 100
          after: $afterCursor
          filter: $filter
          orderBy: $orderBy
        ) {
          edges {
            node {
              ...DraftsList_drafts
            }
          }
          pageInfo {
            endCursor
            hasNextPage
          }
        }
        myDraftsCount: draftsCount(filter: MINE)
        sharedDraftsCount: draftsCount(filter: SHARED)
        archivedDraftsCount: draftsCount(filter: ARCHIVED)
        allDraftsCount: draftsCount(filter: ALL)
      }
    }
  }
`);

const DraftsView = () => {
  const dispatch = useAppDispatch();
  const [filter, setFilter] = useState(DraftsFilter.Mine);
  const orderBy = useAppSelector((state) => state.ui.draftsOrder);
  const setOrderBy = (value: DraftsOrder) =>
    dispatch(UIActions.setDraftsOrder(value));

  const draftsResult = useQuery(DRAFTS_VIEW_QUERY, {
    variables: { filter, orderBy },
  });

  const fetchMoreDrafts = ConnectionHelper.generateFetchMore(
    DRAFTS_VIEW_QUERY,
    draftsResult,
    'currentSession.user.drafts',
  );

  const user = draftsResult.data?.currentSession.user;
  const drafts = _.compact(user?.drafts?.edges?.map((edge) => edge?.node));

  return (
    <PageLayout.Content noPaddingX noPaddingY>
      <PageMeta title="Drafts - Slab" />
      <div className={styles.container}>
        <PageContent>
          <PageTitle
            title="Drafts"
            subtitle="Drafts are only visible to you and those you share with. Publish to make them discoverable by your teammates."
          />
        </PageContent>

        <PostSectionHeader
          controls={
            <>
              <PostFilterMenu
                onChange={setFilter}
                options={{
                  [DraftsFilter.Mine]: {
                    icon: memberIcon,
                    label: 'Mine',
                    count: user?.myDraftsCount,
                  },
                  [DraftsFilter.Shared]: {
                    icon: teamIcon,
                    label: 'Shared',
                    count: user?.sharedDraftsCount,
                  },
                  [DraftsFilter.All]: {
                    icon: draftsIcon,
                    count: user?.allDraftsCount,
                    label: 'All Drafts',
                  },
                  [DraftsFilter.Archived]: {
                    icon: draftArchivedIcon,
                    label: 'Archived',
                    count: user?.archivedDraftsCount,
                  },
                }}
                title="Show"
                value={filter}
              />
              <PostFilterMenu
                icon={orderIcon}
                onChange={setOrderBy}
                options={{
                  [DraftsOrder.RecentlyViewed]: {
                    label: 'Recently viewed',
                  },
                  [DraftsOrder.RecentlyUpdated]: {
                    label: 'Recently updated',
                  },
                  [DraftsOrder.RecentlyCreated]: {
                    label: 'Recently created',
                  },
                  [DraftsOrder.Alphabetical]: {
                    label: 'Alphabetical',
                  },
                }}
                menuPlacement="bottom-end"
                title="Sort"
                value={orderBy}
              />
            </>
          }
        >
          <h3>Posts</h3>
        </PostSectionHeader>
        <PageContent>
          {user && (
            <DraftsList
              drafts={drafts}
              fetchMoreDrafts={fetchMoreDrafts}
              orderBy={orderBy}
            />
          )}
        </PageContent>
      </div>
    </PageLayout.Content>
  );
};

export default DraftsView;
